import { useState } from 'react';
import {
    SocialSignInFacebookDocument,
    SocialSignInFacebookMutation,
    SocialSignInFacebookMutationVariables
} from '../../../queries/facebookSignIn.generated';
import {
    FetchUserWithTrackedPropertiesDocument,
    FetchUserWithTrackedPropertiesQuery,
    FetchUserWithTrackedPropertiesQueryVariables
} from '../../../queries/userWithTrackedProperties.generated';
import { triggerSuccessfulAuthentication } from '../../../../../utils/triggerSuccessfulAuthentication';
import { SignInAction } from '../useSocialSignIn.types';
import { FacebookUserPayload } from './useFacebook.types';
import { triggerTracking } from '@zoopla/tracking';
import { User } from '../../../../../../../graphql.generated';
import { isNewUser } from '../../../../../utils';
import { authTaxonomyObject } from '../../../../../constants/taxonomy';
import { trackAuthSegmentEvent } from '../../../../../utils/segmentTrack';
import { logInfo } from '@zoopla/logger';
import { formatNewRelicErrorData } from '../../../../../utils/formatNewRelicErrorData';
import { formatNewRelicSuccessData } from '../../../../../utils/formatNewRelicSuccessData';
import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { GraphQLError } from '@zoopla/fetch-graphql';
import { setActiveSession } from '@zoopla/user';

export const useFacebook = (
    action?: SignInAction,
    currentUser: Pick<User, 'registered'> = {}
) => {
    const [isFacebookSDKLoaded, setIsFacebookSDKLoaded] = useState(false);
    const [isFacebookAuthenticating, setIsFacebookAuthenticating] =
        useState(false);

    // TODO: Fix undefined being a valid type
    const onGraphQLError = (error: GraphQLError[] | undefined) => {
        if (process.env.NODE_ENV !== 'production' && error) {
            logInfo(error[0].message);
        }
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'signin',
            eventAction: 'failure_generic',
            eventLabel: action?.eventLabel || 'sign_in_modal'
        });
        return action?.onError(
            'Sorry, something went wrong. Please try again.'
        );
    };

    const { lazyFetch: triggerSocialUserTracking } = useLazyFetchGraphql<
        FetchUserWithTrackedPropertiesQuery,
        FetchUserWithTrackedPropertiesQueryVariables
    >(FetchUserWithTrackedPropertiesDocument, {
        async onCompleted(data) {
            if (data?.user?.registrationSources) {
                const isFacebookOnly =
                    data?.user?.registrationSources.length === 1 &&
                    data?.user?.registrationSources[0] === 'facebook';

                triggerTracking({
                    event: 'GAEvent',
                    eventCategory:
                        isFacebookOnly &&
                        isNewUser(currentUser, data?.user.creationDate)
                            ? 'registration'
                            : 'signin',
                    eventAction: 'success - facebook',
                    eventLabel: action?.eventLabel || 'sign_in_modal',
                    taxonomy: authTaxonomyObject.facebook.signin
                });

                await trackAuthSegmentEvent(
                    isFacebookOnly &&
                        isNewUser(currentUser, data?.user.creationDate)
                        ? 'reg'
                        : 'signin',
                    action?.segment,
                    data.user
                );
            }
            // put onSuccess() here as it unmounts the modal component, caused error: `Can't perform a React state update on an unmounted component`
            action?.onSuccess(data?.user || undefined);
        },
        onError() {
            // The user query starts when sign in mutation is completed successfully.
            // User is already logged in at this moment. Trigger onSuccess here to close the authModal.
            action?.onSuccess();
        }
    });

    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SocialSignInFacebookMutation,
        SocialSignInFacebookMutationVariables
    >(SocialSignInFacebookDocument, {
        onCompleted({ socialSignInFacebook }) {
            setIsFacebookAuthenticating(false);

            if (!socialSignInFacebook?.success) {
                action?.onError(
                    'Sorry, something went wrong. Please try again.'
                );
                triggerTracking({
                    event: 'GAEvent',
                    eventCategory: 'signin',
                    eventAction: 'failure_generic',
                    eventLabel: action?.eventLabel || 'sign_in_modal'
                });
                return;
            }
            setActiveSession();
            triggerSuccessfulAuthentication();
            triggerSocialUserTracking();
        },
        onError(error) {
            onGraphQLError(error);
        }
    });

    const setFbAsyncInit = () => {
        window.fbAsyncInit = () => {
            window.FB?.init({
                appId: '205940246178878',
                oauth: true,
                cookie: true, // enable cookies to allow the server to access the session
                version: 'v10.0'
            });
            setIsFacebookSDKLoaded(true);
        };
    };

    const loadSdk = () => {
        (() => {
            const element = document.getElementsByTagName('script')[0];
            const sdk = document.createElement('script');
            sdk.src = `https://connect.facebook.net/en_US/sdk.js`;
            element?.parentNode?.insertBefore(sdk, element);
        })();
    };

    const init = () => {
        setFbAsyncInit();
        loadSdk();
    };

    const onLogin = (rememberMe?: boolean) => {
        setIsFacebookAuthenticating(true);

        if (!isFacebookSDKLoaded) {
            init();
        }

        try {
            window.FB?.login(
                function (response: FacebookUserPayload) {
                    if (
                        response.status === 'connected' &&
                        response?.authResponse
                    ) {
                        signInMutation({
                            accessToken: response?.authResponse?.accessToken,
                            rememberMe: rememberMe || false
                        });
                        window.newrelic?.addPageAction(
                            ...formatNewRelicSuccessData({
                                name: 'facebook-auth'
                            })
                        );
                    } else {
                        if (process.env.NODE_ENV !== 'production') {
                            logInfo(response);
                        }
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'signin',
                            eventAction: 'failure_facebook',
                            eventLabel: action?.eventLabel || 'sign_in_modal'
                        });

                        window.newrelic?.addPageAction(
                            ...formatNewRelicErrorData({
                                name: 'facebook-auth',
                                type:
                                    response.status === 'connected'
                                        ? 'unknown'
                                        : response.status
                            })
                        );

                        setIsFacebookAuthenticating(false);

                        return action?.onError(
                            'Something went wrong and you have not been signed in. Please try again.'
                        );
                    }
                },
                { scope: 'public_profile,email' }
            );
        } catch (e) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'signin',
                eventAction: 'failure_facebook',
                eventLabel: action?.eventLabel || 'sign_in_modal'
            });

            window.newrelic?.addPageAction(
                ...formatNewRelicErrorData({
                    name: 'facebook-auth',
                    type: 'unknown'
                })
            );

            setIsFacebookAuthenticating(false);

            return action?.onError(
                'Sorry, something went wrong. Please try again.'
            );
        }
    };

    return {
        init,
        onLogin,
        isAuthenticating: isFacebookAuthenticating
    };
};
